import { __awaiter } from "tslib";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import useOnClickOutside from '../../hooks/use-on-click-outside';
import { directionAlias, toClickOutside } from './utils';
import { arrow, offset, overflow, adaptiveDirection, silent, createPopper } from './popper';
import s from './tooltip.module.css';
import useTransitionState from '../../hooks/use-transition-state';
import useDOMRoot from '../../hooks/use-dom-root';
const closingTimeout = 240;
const Launchpad = ({ root, children }) => {
    return root ? createPortal(children, root) : children;
};
export const Tooltip = ({ to = { current: new Set() }, container = { current: null }, trigger = 'hover', color = 'black', direction = 'up', placement = 'local', arrow: showArrow = true, children, enableAnimation = true, }) => {
    var _a;
    const [visible, setVisible] = useState(false);
    const tooltip = useRef(null);
    const event = useRef();
    const [state, close] = useTransitionState(visible, { duration: closingTimeout });
    const root = useDOMRoot({ anchor: 'tooltip-launchpad', style: s.root });
    const popper = useRef(null);
    const show = useCallback((evt) => __awaiter(void 0, void 0, void 0, function* () {
        if (visible) {
            return;
        }
        event.current = evt;
        setVisible(true);
    }), [visible]);
    const hide = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (visible && state === 'show') {
            yield close();
        }
        setVisible(false);
    }), [visible, state, close]);
    useEffect(() => {
        var _a;
        if (!to.current || state !== 'appear' || !event.current) {
            return;
        }
        const ref = ((trigger === 'click' || trigger === 'static') &&
            Array.from(to.current).find(el => { var _a; return el.contains((_a = event.current) === null || _a === void 0 ? void 0 : _a.target); })) ||
            event.current.target;
        if (popper.current) {
            (_a = popper.current) === null || _a === void 0 ? void 0 : _a.destroy();
        }
        popper.current = createPopper(ref, tooltip.current, {
            placement: directionAlias[direction],
            modifiers: [
                ...(showArrow ? [arrow] : []),
                offset,
                overflow(container.current || document.body),
                adaptiveDirection(container.current, direction),
            ],
        });
    }, [state, to, direction, trigger, container, showArrow]);
    useEffect(() => {
        var _a;
        if (state !== 'idle') {
            return;
        }
        (_a = popper.current) === null || _a === void 0 ? void 0 : _a.setOptions({ modifiers: [...silent] });
    }, [state]);
    useOnClickOutside(toClickOutside(popper), hide, trigger === 'click');
    useEffect(() => {
        const controls = to.current;
        if (!controls || controls.size === 0) {
            return;
        }
        if (trigger === 'static') {
            controls.forEach((item) => {
                show({ target: item });
            });
            return;
        }
        const handlerType = trigger === 'click' ? 'click' : 'mouseover';
        controls.forEach((item) => {
            item.addEventListener(handlerType, show);
            if (trigger === 'hover') {
                item.addEventListener('mouseleave', hide);
            }
        });
        return () => {
            controls.forEach((item) => {
                item.removeEventListener(handlerType, show);
                item.removeEventListener('mouseleave', hide);
            });
        };
    }, [trigger, (_a = to.current) === null || _a === void 0 ? void 0 : _a.size]);
    if (state === 'init' || state === 'idle') {
        return null;
    }
    return (React.createElement(Launchpad, { root: placement === 'body' ? root : null },
        React.createElement("div", { ref: tooltip, className: classNames(s.content, { [s.animation]: enableAnimation }, s[color], state === 'show' && s.visible, placement === 'local' && s.local) },
            children,
            showArrow && React.createElement("div", { className: s.arrow, "data-popper-arrow": true }))));
};
//# sourceMappingURL=tooltip.js.map